<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b sticky top-0 z-10">
      <div class="container mx-auto px-4 lg:px-6 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-folder-open text-blue-600 text-3xl mr-4"></i>
            <div>
              <h1 class="text-2xl lg:text-3xl font-bold text-gray-800">
                {{ publicApp?.name || 'Loading...' }}
              </h1>
              <div class="text-sm lg:text-base text-gray-600 mt-1">
                <span class="mr-6">Public File Repository</span>
                <span class="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-xs lg:text-sm rounded-full">
                  <i class="fas fa-globe mr-1"></i>
                  Public
                </span>
              </div>
            </div>
          </div>
          
          <div class="flex items-center gap-3">
            <div class="hidden md:block text-sm lg:text-base text-gray-600">
              <i class="fas fa-user mr-2"></i>
              by {{ publicApp?.user?.name || publicApp?.user?.email }}
            </div>
            
            <!-- Action buttons -->
            <div class="flex items-center gap-2">
              <button
                @click="refreshContent"
                :disabled="loading"
                class="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm lg:text-base touch-manipulation"
                title="Refresh content"
              >
                <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
                <span class="hidden sm:inline ml-2">Refresh</span>
              </button>
              
              <button
                @click="shareApp"
                class="px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors text-sm lg:text-base touch-manipulation"
                title="Share this app"
              >
                <i class="fas fa-share mr-2"></i>
                <span class="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="container mx-auto px-4 lg:px-6 py-6 lg:py-8">
      <!-- Error Message -->
      <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6 lg:mb-8">
        <i class="fas fa-exclamation-circle mr-3 text-lg"></i>
        <strong>Error:</strong> {{ error }}
      </div>

      <div class="max-w-6xl mx-auto">
        <!-- Content -->
        <div v-if="publicApp" class="bg-white rounded-2xl shadow-lg overflow-hidden">
          <!-- Content header with stats -->
          <div class="border-b border-gray-100 px-6 lg:px-8 py-6">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-6 lg:gap-8">
                <div class="flex items-center text-sm lg:text-base text-gray-600">
                  <i class="fas fa-file-alt mr-2 text-blue-500"></i>
                  <span class="font-medium">{{ getTotalFiles() }}</span>
                  <span class="ml-1">files</span>
                </div>
                <div class="flex items-center text-sm lg:text-base text-gray-600">
                  <i class="fas fa-folder mr-2 text-amber-500"></i>
                  <span class="font-medium">{{ appStructure.folders.length }}</span>
                  <span class="ml-1">folders</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- File content -->
          <div class="p-6 lg:p-8">
            <PublicFolderTree 
              :items="formatStructureForTree()"
              :id="id"
              @message="showMessage"
            />
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="bg-white rounded-2xl shadow-lg p-16 text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p class="text-gray-600 mt-6 text-lg">Loading app content...</p>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div v-if="message" class="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-4 rounded-xl shadow-xl z-50 max-w-sm">
      <i class="fas fa-check-circle mr-2"></i>
      {{ message }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import PublicFolderTree from '../components/PublicFolderTree.vue'
import { PublicApiService } from '../services/publicApiService'
import type { PublicApp, FileItem } from '../types/api'
import type { AppStructure } from '../services/apiService'

const route = useRoute()
const id = route.params.id as string

interface FileStructureItem {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  mimeType?: string
  children?: FileStructureItem[]
  expanded?: boolean
  loading?: boolean
  childrenLoaded?: boolean
  path?: string
}

const publicApp = ref<PublicApp | null>(null)
const appStructure = ref<AppStructure>({
  folders: [],
  files: []
})
const loading = ref<boolean>(false)
const error = ref<string | null>(null)
const message = ref<string | null>(null)

const fetchPublicApp = async (): Promise<void> => {
  try {
    publicApp.value = await PublicApiService.getPublicApp(id)
  } catch (err: any) {
    error.value = err.error || 'Failed to load app'
  }
}

const fetchAppStructure = async (): Promise<void> => {
  loading.value = true
  error.value = null

  try {
    const structure = await PublicApiService.getPublicAppStructure(id)
    appStructure.value = structure
    console.log('App structure loaded:', structure)
  } catch (err: any) {
    console.error('Failed to fetch structure:', err)
    error.value = err.error || 'Failed to load app structure'
  } finally {
    loading.value = false
  }
}

const refreshContent = async () => {
  await fetchAppStructure()
  showMessage('Content refreshed successfully!')
}

const shareApp = async () => {
  try {
    const currentUrl = window.location.href
    await navigator.clipboard.writeText(currentUrl)
    showMessage('Share link copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy share link:', err)
    showMessage('Failed to copy share link')
  }
}

const showMessage = (text: string) => {
  message.value = text
  setTimeout(() => {
    message.value = null
  }, 4000)
}

const getTotalFiles = (): number => {
  // Count all files including nested ones
  let count = appStructure.value.files.length
  
  const countFilesInFolders = (folders: any[]): number => {
    let fileCount = 0
    folders.forEach(folder => {
      fileCount += folder.files.length
      if (folder.folders && folder.folders.length > 0) {
        fileCount += countFilesInFolders(folder.folders)
      }
    })
    return fileCount
  }
  
  count += countFilesInFolders(appStructure.value.folders)
  return count
}

const formatStructureForTree = (): FileStructureItem[] => {
  const items: FileStructureItem[] = []
  
  // Add root level folders
  appStructure.value.folders.forEach(folder => {
    items.push(formatFolderForTree(folder))
  })
  
  // Add root level files
  appStructure.value.files.forEach(file => {
    items.push({
      id: file.id,
      name: file.name,
      type: 'file',
      size: file.size,
      mimeType: file.mimeType
    })
  })
  
  return items
}

const formatFolderForTree = (folder: any): FileStructureItem => {
  const children: FileStructureItem[] = []
  
  // Add subfolders
  if (folder.folders) {
    folder.folders.forEach((subfolder: any) => {
      children.push(formatFolderForTree(subfolder))
    })
  }
  
  // Add files in this folder
  if (folder.files) {
    folder.files.forEach((file: FileItem) => {
      children.push({
        id: file.id,
        name: file.name,
        type: 'file',
        size: file.size,
        mimeType: file.mimeType
      })
    })
  }
  
  return {
    id: folder.id,
    name: folder.name,
    type: 'folder',
    children,
    expanded: false,
    loading: false,
    childrenLoaded: true
  }
}

onMounted(async () => {
  await Promise.all([
    fetchPublicApp(),
    fetchAppStructure()
  ])
})
</script>

<style scoped>
.touch-manipulation {
  touch-action: manipulation;
}
</style> 